// src/pages/admin/users/adminUsersApi.js
import { auth } from "../../../utils/firebase";

const DAY_MS = 24 * 60 * 60 * 1000;

const CANONICAL_PLANS = ["basic", "pro", "premium"];

/* ===================== apiBase detect ("" or http://localhost:3001) ===================== */
let _apiBase = null;

async function detectApiBase() {
  if (_apiBase !== null) return _apiBase;

  const candidates = ["", "http://localhost:3001"];
  for (const base of candidates) {
    try {
      const res = await fetch(`${base}/api/_selftest`);
      if (res.ok) {
        _apiBase = base;
        return _apiBase;
      }
    } catch {
      // try next
    }
  }

  // fallback: keep "" so it still works if you have CRA proxy
  _apiBase = "";
  return _apiBase;
}

/* ===================== token ===================== */
async function getToken(forceRefresh = false) {
  if (!auth) throw new Error("Firebase auth chưa khởi tạo (auth = null).");
  const user = auth.currentUser;
  if (!user) throw new Error("Chưa đăng nhập admin.");
  return await user.getIdToken(!!forceRefresh);
}

function buildQuery(params = {}) {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null) return;
    const s = String(v).trim();
    if (!s) return;
    qs.set(k, s);
  });
  const str = qs.toString();
  return str ? `?${str}` : "";
}

async function apiFetch(path, options = {}, _retry = true) {
  const apiBase = await detectApiBase();

  // token: lần đầu không force refresh để nhẹ
  const token = await getToken(false);

  const headers = {
    Accept: "application/json",
    Authorization: `Bearer ${token}`,
    ...(options.headers || {}),
  };

  const hasBody = options.body !== undefined && options.body !== null;
  if (hasBody && !headers["Content-Type"]) headers["Content-Type"] = "application/json";

  const url = `${apiBase}${path}`;
  const res = await fetch(url, { ...options, headers });

  const text = await res.text().catch(() => "");
  let data = {};
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    data = text ? { raw: text } : {};
  }

  // eslint-disable-next-line no-console
  console.log("[ADMIN API]", options.method || "GET", url, "=>", res.status, data);

  // Nếu 401/invalid_token => refresh token rồi retry 1 lần
  const errCode = String(data?.error || "").toLowerCase();
  const errMsg = String(data?.message || "").toLowerCase();
  const looksLikeInvalidToken =
    res.status === 401 && (errCode.includes("token") || errMsg.includes("token") || errCode.includes("invalid"));

  if (!res.ok && _retry && looksLikeInvalidToken) {
    const refreshed = await getToken(true);

    const headers2 = {
      ...headers,
      Authorization: `Bearer ${refreshed}`,
    };

    const res2 = await fetch(url, { ...options, headers: headers2 });
    const text2 = await res2.text().catch(() => "");
    let data2 = {};
    try {
      data2 = text2 ? JSON.parse(text2) : {};
    } catch {
      data2 = text2 ? { raw: text2 } : {};
    }

    // eslint-disable-next-line no-console
    console.log("[ADMIN API RETRY]", options.method || "GET", url, "=>", res2.status, data2);

    if (!res2.ok) {
      const msg =
        data2?.message || data2?.error || `HTTP ${res2.status} (Có thể bạn chưa có quyền admin)`;
      const e = new Error(msg);
      e.status = res2.status;
      e.data = data2;
      throw e;
    }
    return data2;
  }

  if (!res.ok) {
    const msg = data?.message || data?.error || `HTTP ${res.status} (Có thể bạn chưa có quyền admin)`;
    const err = new Error(msg);
    err.status = res.status;
    err.data = data;
    throw err;
  }

  return data;
}

/**
 * GET /api/admin/users?limit=50&pageToken=...&q=...&status=all|active|stop&createdBy=...
 */
export async function fetchUsers({ limit = 50, pageToken, q, status = "all", createdBy } = {}) {
  const query = buildQuery({ limit, pageToken, q, status, createdBy });
  return apiFetch(`/api/admin/users${query}`);
}

/** GET /api/admin/users/me — current operator (normalizedRole from backend; source of truth) */
export async function fetchCurrentOperator() {
  return apiFetch("/api/admin/users/me");
}

/** @deprecated use fetchCurrentOperator */
export async function fetchAdminOperator() {
  return fetchCurrentOperator();
}

/** GET /api/admin/users/:uid */
export async function getUser(uid) {
  if (!uid) throw new Error("uid is required");
  return apiFetch(`/api/admin/users/${encodeURIComponent(uid)}`);
}

/** POST /api/admin/users */
export async function createUser(payload) {
  return apiFetch("/api/admin/users", {
    method: "POST",
    body: JSON.stringify(payload || {}),
  });
}

/** POST /api/admin/users/:uid/disable */
export async function disableUser(uid) {
  if (!uid) throw new Error("uid is required");
  return apiFetch(`/api/admin/users/${encodeURIComponent(uid)}/disable`, { method: "POST" });
}

/** POST /api/admin/users/:uid/enable */
export async function enableUser(uid) {
  if (!uid) throw new Error("uid is required");
  return apiFetch(`/api/admin/users/${encodeURIComponent(uid)}/enable`, { method: "POST" });
}

/** PATCH /api/admin/users/:uid */
export async function updateUser(uid, patch) {
  if (!uid) throw new Error("uid is required");
  return apiFetch(`/api/admin/users/${encodeURIComponent(uid)}`, {
    method: "PATCH",
    body: JSON.stringify(patch || {}),
  });
}

/** PATCH plan only — canonical: basic | pro | premium */
export async function setUserPlan(uid, plan) {
  if (!uid) throw new Error("uid is required");
  const p = String(plan || "").trim().toLowerCase();
  if (!CANONICAL_PLANS.includes(p)) throw new Error("plan must be basic, pro, or premium");
  return updateUser(uid, { plan: p });
}

/** PATCH role — canonical: user | admin | super.admin */
export async function setUserRole(uid, role) {
  if (!uid) throw new Error("uid is required");
  const r = String(role || "").trim().toLowerCase();
  if (r !== "user" && r !== "admin" && r !== "super.admin") {
    throw new Error("role must be user, admin, or super.admin");
  }
  return updateUser(uid, { role: r });
}

/** DELETE /api/admin/users/:uid */
export async function deleteUser(uid) {
  if (!uid) throw new Error("uid is required");
  return apiFetch(`/api/admin/users/${encodeURIComponent(uid)}`, { method: "DELETE" });
}

/* ===================== Helpers days ===================== */
export async function setUserDays(uid, days) {
  const n = Number(days);
  if (!Number.isFinite(n) || n < 0) throw new Error("days phải là số >= 0");

  const expiresAt = Date.now() + Math.floor(n) * DAY_MS;
  return updateUser(uid, {
    access: { mode: "days", expiresAt },
    analysisAccess: { blocked: false },
  });
}

export async function applyDeltaDays(uid, currentDays, delta) {
  const cur = Number(currentDays);
  const d = Number(delta);
  if (!Number.isFinite(cur) || cur < 0) throw new Error("currentDays phải là số >= 0");
  if (!Number.isFinite(d) || d === 0) throw new Error("delta phải là số khác 0");

  const nextDays = Math.max(0, Math.floor(cur + d));
  const expiresAt = Date.now() + nextDays * DAY_MS;

  return updateUser(uid, {
    access: { mode: "days", expiresAt },
    analysisAccess: { blocked: false },
  });
}
